import type { ObstacleState, Vec3, VehicleState } from './models';

interface Footprint {
  halfX: number;
  halfZ: number;
}

const BOX_FOOTPRINTS: Partial<Record<ObstacleState['kind'], Footprint>> = {
  barrier: { halfX: 4.5, halfZ: 1.4 },
  relay: { halfX: 1.7, halfZ: 1.7 },
};

export interface SweepHit {
  obstacle: ObstacleState;
  fraction: number;
  point: Vec3;
}

function toObstacleSpace(point: Vec3, obstacle: ObstacleState): { x: number; z: number } {
  const dx = point.x - obstacle.position.x;
  const dz = point.z - obstacle.position.z;
  const cosine = Math.cos(obstacle.yaw);
  const sine = Math.sin(obstacle.yaw);
  return { x: dx * cosine + dz * sine, z: -dx * sine + dz * cosine };
}

export function circleIntersectsObstacle(
  position: Pick<Vec3, 'x' | 'z'>,
  radius: number,
  obstacle: ObstacleState,
): boolean {
  const footprint = BOX_FOOTPRINTS[obstacle.kind];
  if (!footprint) {
    return Math.hypot(position.x - obstacle.position.x, position.z - obstacle.position.z) < radius + obstacle.radius;
  }
  const local = toObstacleSpace({ ...position, y: 0 }, obstacle);
  const closestX = Math.max(-footprint.halfX, Math.min(footprint.halfX, local.x));
  const closestZ = Math.max(-footprint.halfZ, Math.min(footprint.halfZ, local.z));
  return Math.hypot(local.x - closestX, local.z - closestZ) < radius;
}

export function collidesWithAnyObstacle(
  position: Pick<Vec3, 'x' | 'z'>,
  radius: number,
  obstacles: readonly ObstacleState[],
): boolean {
  return obstacles.some((obstacle) => circleIntersectsObstacle(position, radius, obstacle));
}

export function moveVehicleWithCollisions(
  vehicle: VehicleState,
  movement: Pick<Vec3, 'x' | 'z'>,
  obstacles: readonly ObstacleState[],
): { position: Vec3; bumped: boolean } {
  let x = vehicle.position.x;
  let z = vehicle.position.z;
  let bumped = false;
  const nextX = x + movement.x;
  if (!collidesWithAnyObstacle({ x: nextX, z }, vehicle.radius, obstacles)) x = nextX;
  else bumped = true;
  const nextZ = z + movement.z;
  if (!collidesWithAnyObstacle({ x, z: nextZ }, vehicle.radius, obstacles)) z = nextZ;
  else bumped = true;
  return { position: { x, y: vehicle.position.y, z }, bumped };
}

export function segmentCircleFraction(start: Vec3, end: Vec3, center: Vec3, radius: number): number | null {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const fx = start.x - center.x;
  const fz = start.z - center.z;
  const c = fx * fx + fz * fz - radius * radius;
  if (c <= 0) return 0;
  const a = dx * dx + dz * dz;
  if (a === 0) return null;
  const b = 2 * (fx * dx + fz * dz);
  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return null;
  const root = Math.sqrt(discriminant);
  const first = (-b - root) / (2 * a);
  const second = (-b + root) / (2 * a);
  if (first >= 0 && first <= 1) return first;
  if (second >= 0 && second <= 1) return second;
  return null;
}

export function segmentSphereFraction(start: Vec3, end: Vec3, center: Vec3, radius: number): number | null {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const dz = end.z - start.z;
  const fx = start.x - center.x;
  const fy = start.y - center.y;
  const fz = start.z - center.z;
  const c = fx * fx + fy * fy + fz * fz - radius * radius;
  if (c <= 0) return 0;
  const a = dx * dx + dy * dy + dz * dz;
  if (a === 0) return null;
  const b = 2 * (fx * dx + fy * dy + fz * dz);
  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return null;
  const root = Math.sqrt(discriminant);
  const first = (-b - root) / (2 * a);
  const second = (-b + root) / (2 * a);
  if (first >= 0 && first <= 1) return first;
  if (second >= 0 && second <= 1) return second;
  return null;
}

export function movingSphereFraction(
  firstStart: Vec3,
  firstEnd: Vec3,
  secondStart: Vec3,
  secondEnd: Vec3,
  radius: number,
): number | null {
  return segmentSphereFraction(
    {
      x: firstStart.x - secondStart.x,
      y: firstStart.y - secondStart.y,
      z: firstStart.z - secondStart.z,
    },
    {
      x: firstEnd.x - secondEnd.x,
      y: firstEnd.y - secondEnd.y,
      z: firstEnd.z - secondEnd.z,
    },
    { x: 0, y: 0, z: 0 },
    radius,
  );
}

function segmentBoxFraction(start: Vec3, end: Vec3, obstacle: ObstacleState, footprint: Footprint, radius: number): number | null {
  const localStart = toObstacleSpace(start, obstacle);
  const localEnd = toObstacleSpace(end, obstacle);
  const dx = localEnd.x - localStart.x;
  const dz = localEnd.z - localStart.z;
  const minX = -footprint.halfX - radius;
  const maxX = footprint.halfX + radius;
  const minZ = -footprint.halfZ - radius;
  const maxZ = footprint.halfZ + radius;
  let enter = 0;
  let exit = 1;
  const edges: readonly [number, number][] = [
    [-dx, localStart.x - minX], [dx, maxX - localStart.x],
    [-dz, localStart.z - minZ], [dz, maxZ - localStart.z],
  ];
  for (const [p, q] of edges) {
    if (p === 0) {
      if (q < 0) return null;
      continue;
    }
    const ratio = q / p;
    if (p < 0) enter = Math.max(enter, ratio);
    else exit = Math.min(exit, ratio);
    if (enter > exit) return null;
  }
  return enter;
}

export function sweepObstacles(
  start: Vec3,
  end: Vec3,
  radius: number,
  obstacles: readonly ObstacleState[],
): SweepHit | null {
  let nearest: SweepHit | null = null;
  for (const obstacle of obstacles) {
    const footprint = BOX_FOOTPRINTS[obstacle.kind];
    const fraction = footprint
      ? segmentBoxFraction(start, end, obstacle, footprint, radius)
      : segmentCircleFraction(start, end, obstacle.position, obstacle.radius + radius);
    if (fraction === null || (nearest && fraction >= nearest.fraction)) continue;
    nearest = {
      obstacle,
      fraction,
      point: {
        x: start.x + (end.x - start.x) * fraction,
        y: start.y + (end.y - start.y) * fraction,
        z: start.z + (end.z - start.z) * fraction,
      },
    };
  }
  return nearest;
}
